/**
 * Page to allow a reseller, customer or vendor to top off their asset account.
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import AbEnvironment from '../auth/AbEnvironment'; 

import PropTypes from 'prop-types';
import { Grid, Paper, Switch, Tooltip, Typography } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import HelpOutline from '@mui/icons-material/HelpOutline';

import ServerError from '../ui/ServerError';

import CurrencyTextField from '../ui/CurrencyTextField';

import LoadingSpinner from '../ui/LoadingSpinner';
import SaveButton from '../ui/SaveButton';
import BalanceAndFloatWidgets from '../home/BalanceAndFloatWidgets';
import SpendingWidgets from '../home/SpendingWidgets';
import FundingSourceChargeLimitUpdateMutation from './FundingSourceChargeLimitUpdateMutation';

import PoweredBy from '../landing/PoweredBy';

import { green } from '@mui/material/colors';

const abEnvironment = new AbEnvironment();

const FundingSourceChargeLimitQuery = graphql`
query FundingSourceChargeLimitQuery($accountId: UUID!) {
  accountTByAccountId(accountId: $accountId) {
    accountType
    payFromBalance
    maxMonthlyBudget
    useBudgets
    floatLimit
    autoTopupActive
    autoTopupAmount
    autoTopupThreshhold
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
  },
  sectionBlock: {
    marginBottom: 12,  
  },
  paper: {
    textAlign: 'left',
    //color: theme.palette.text.secondary,
    maxWidth: 1122,
    marginTop: 16,
    marginBottom: 16,
    paddingTop: 16,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
  },
  currencyField: {
    width: 88,
    marginTop: 4,
    marginBottom: 4,
    marginRight: 8,
  },
  noBudgetHolder: {
    width: 88,
    marginTop: 8,
    marginBottom: 8,
    marginRight: 8,
    textAlign: 'right',
  },
  success: {
    backgroundColor: green[300],
  },
  error: {
    backgroundColor: theme.palette.error.light,
  },
});

class FundingSourceChargeLimit extends Component {
  constructor(props)
  {
    super(props);
    this.state = {
      payFromBalance: null,
      maxMonthlyBudget: null,
      maxMonthlyBudgetSet: false,

      savedPayFromBalance: null, // For save button behavior post save
      savedMaxMonthlyBudget: null, // For save button behavior post save
      savedMaxMonthlyBudgetSet: false,

      // Save statuses for limits
      saveStatusLimit: null,
      saveMessageLimit: null,
      saveSnackbarOpenLimit: false,
      saveSnackbarDurationLimit: null,
    };
  }

  isLimitSaveDisabled(theAccount) {
    // Compare against the saved value.
    let theSavedPayFromBalance = theAccount.payFromBalance;
    if (this.state.savedPayFromBalance != null) {
      theSavedPayFromBalance = this.state.savedPayFromBalance;
    }

    let theSavedMaxMonthlyBudget = theAccount.maxMonthlyBudget;
    if (this.state.savedMaxMonthlyBudgetSet === true) {
      theSavedMaxMonthlyBudget = this.state.savedMaxMonthlyBudget;
    }

    if ( (this.state.payFromBalance == null || theSavedPayFromBalance === this.state.payFromBalance) &&
         (this.state.maxMonthlyBudgetSet === false || 
          (theSavedMaxMonthlyBudget == null && this.state.maxMonthlyBudget == null) ||
          (parseFloat(theSavedMaxMonthlyBudget) === parseFloat(this.state.maxMonthlyBudget))
         )  ) 
    {
      return true;
    }

    // Do a validation check on the monthly budget amount
    if (this.state.maxMonthlyBudget != null && (this.state.maxMonthlyBudget < 40 || this.state.maxMonthlyBudget > 9999)) {
      return true;
    }

    return false;
  }

  togglePayFromBalance(payFromBalance) {
    if (payFromBalance === 0) {
      this.setState({ payFromBalance: 1 });
    } else {
      this.setState({ payFromBalance: 0 });
    }
  }

  toggleSetMonthlyLimit(theAccount, useMonthlySpend) {
    if (useMonthlySpend) {
      // Toggle to off
      this.setState({maxMonthlyBudget: null, maxMonthlyBudgetSet: true});
    } else {
      // Toggle to on
      if (theAccount.maxMonthlyBudget != null) {
        this.setState({maxMonthlyBudget: theAccount.maxMonthlyBudget, maxMonthlyBudgetSet: true});
      } else {
        this.setState({maxMonthlyBudget: 500.00, maxMonthlyBudgetSet: true});
      }
    }
  }

  render() {
    const classes = this.props.classes;
    
    const { logError } = this.props.auth;

    let acctId = this.props.accountId;
    let acctType = this.props.accountType;

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={FundingSourceChargeLimitQuery}
        variables={{accountId: acctId}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {
            let theAccount = props.accountTByAccountId;

            /* The only thing we're managing on the charge limits is using balance to pay (if a reseller), and
               setting monthly spend control */
            let payFromBalance = theAccount.payFromBalance;
            if (this.state.payFromBalance != null) {
              payFromBalance = this.state.payFromBalance;
            }

            /* monthly spend control */
            let useMonthlySpend = false;
            let origMonthlySpend = theAccount.maxMonthlyBudget;
            let monthlySpend = origMonthlySpend;

            if (this.state.maxMonthlyBudgetSet === true) {
              monthlySpend = this.state.maxMonthlyBudget;
            }

            if (monthlySpend != null) {
              useMonthlySpend = true;
            }

            //for "pay out balance", need to grab autoTopup fields from account:
            let autoTopupActive = theAccount.autoTopupActive;
            let autoTopupAmount = null;
            let autoTopupThreshold = null;
            if (autoTopupActive === 1) {
              if (Number(theAccount.autoTopupAmount) > 0 && Number(theAccount.autoTopupThreshhold) > 0) {
                autoTopupAmount = Number(theAccount.autoTopupAmount);
                autoTopupThreshold = Number(theAccount.autoTopupThreshhold);
              }
            }
            
            return (
              <React.Fragment>
                <Grid container spacing={2} direction="row" justifyContent="flex-start" className={classes.sectionBlock}>
                  <Grid item xs={12}>
                    <Grid container spacing={0} direction="column" justifyContent="flex-start">
                      <Grid item>
                        <Typography variant="h6">Manage charge limits</Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="body2">View charge limit and subscription payment options</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Paper className={classes.paper}>
                  <Grid container spacing={2} justifyContent="flex-start">
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.sectionTitle}>Your AppBind balance</Typography>
                      <Typography variant="body2">
                        AppBind provides you with a float to pay for subscriptions and services.  You may also use your available account balance to pay.
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <BalanceAndFloatWidgets 
                        auth={this.props.auth}
                        autoTopupAmount={autoTopupAmount}
                        autoTopupThreshold={autoTopupThreshold}
                        showLimitRequest={true}
                        floatLimit={theAccount.floatLimit}
                        accountId={acctId}
                        accountType={acctType}
                        useBudgets={theAccount.useBudgets}
                        narrow={true}
                      />
                    </Grid>
                    {(acctType !== '4') &&
                    <Grid item xs={12}>
                      <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                        <Grid item>
                          <Grid container justifyContent="flex-start" alignItems="center" spacing={0}>
                            <Grid item>
                              <Typography variant="body1">Use my balance to pay</Typography>
                            </Grid>
                            <Grid item>
                              <Tooltip placement='bottom' title={"We'll use your balance to pay for subscription charges before billing your funding source."}>
                                <HelpOutline fontSize="small" style={{verticalAlign: "-7%", marginLeft: '4px'}}/>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item>
                          <Switch checked={payFromBalance === 1} 
                                  onChange={(e) => this.togglePayFromBalance(payFromBalance)} 
                                  value={payFromBalance}
                                  color="primary"
                          />
                        </Grid>
                      </Grid>
                    </Grid>}

                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.sectionTitle}>Your spending</Typography>
                      <Typography variant="body2">
                        AppBind tracks your spending and provides spend controls.
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <SpendingWidgets accountId={acctId} spendLimit={monthlySpend}/>
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                        <Grid item>
                          <Grid container justifyContent="flex-start" alignItems="center" spacing={0}>
                            <Grid item>
                              <Typography variant="body1">Set a monthly spend limit</Typography>
                            </Grid>
                            <Grid item>
                              <Tooltip placement='bottom' title={"If you set a monthly spend limit, we'll decline any subscription charges that would cause you to spend more than this limit in a calendar month."}>
                                <HelpOutline fontSize="small" style={{verticalAlign: "-7%", marginLeft: '4px'}}/>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item>
                          <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                            <Grid item>
                              {useMonthlySpend === true &&
                              <CurrencyTextField
                                      id="maxMonthlyBudget" 
                                      className={classes.currencyField}
                                      value={monthlySpend}
                                      showErrMsg={true}
                                      allowNegative={false}
                                      minValue={40}
                                      maxValue={9999}
                                      minEnterValue={0}
                                      maxEnterValue={99999999}
                                      onChange={(e) => this.setState({ maxMonthlyBudget: e.target.value, maxMonthlyBudgetSet: true })}
                                      textAlign="right"
                              /> }
                              {useMonthlySpend !== true &&
                              <Typography className={classes.noBudgetHolder} variant="body1"><i>No limit</i></Typography>}
                            </Grid>
                            <Grid item>
                              <Switch checked={useMonthlySpend === true} 
                                      onChange={(e) => this.toggleSetMonthlyLimit(theAccount, useMonthlySpend)} 
                                      value={useMonthlySpend}
                                      color="primary"
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <SaveButton
                        snackbarOpen={this.state.saveSnackbarOpenLimit}
                        autoHideDuration={this.state.saveSnackbarDurationLimit}
                        onSaveClick={() => { this._updateLimits(theAccount); }}
                        onSnackbarClose={() => { this._onSnackbarClose(); }}
                        status={this.state.saveStatusLimit}
                        message={this.state.saveMessageLimit}
                        disabled={this.isLimitSaveDisabled(theAccount)}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {this.props.auth.isUnauthenticatedCustomer() && false &&
                <Grid  container direction="row" justifyContent="center" alignItems="center">
                  <PoweredBy/>
                </Grid>}
              </React.Fragment>
            );
          }
          return <div><LoadingSpinner/></div>
        }}
      />
    );
  }
  
  _onSnackbarClose() {
    this.setState({saveSnackbarOpenLimit: false, saveStatusLimit: '', saveMessageLimit: '', saveSnackbarDurationLimit: null });
  }

  _updateLimits(theAccount) {
    let accountId = this.props.accountId

    this.setState({ saveStatusLimit: 'saving' });
    const { payFromBalance, maxMonthlyBudget, maxMonthlyBudgetSet } = this.state;

    FundingSourceChargeLimitUpdateMutation.commit(accountId, payFromBalance, maxMonthlyBudgetSet, maxMonthlyBudget)
    .then(response => {
      // console.log('Account updated')
      let theState = {
        saveSnackbarOpenLimit: true, 
        saveStatusLimit: 'success', 
        saveMessageLimit: 'Account limits saved', 
        saveSnackbarDurationLimit: 3000,
        savedPayFromBalance: payFromBalance
      };

      if (maxMonthlyBudgetSet) {
        theState.savedMaxMonthlyBudget = maxMonthlyBudget;
        theState.savedMaxMonthlyBudgetSet = true;
      }

      this.setState(theState);
    })
    .catch(error => {
      // console.log('Got error: ' + error);
      this.setState({ saveSnackbarOpenLimit: true, saveStatusLimit: 'error', saveMessageLimit: 'Problem saving account limits', saveSnackbarDurationLimit: null  });
    });
  }
}
  
FundingSourceChargeLimit.propTypes = {
    classes: PropTypes.object.isRequired,
  };
      
export default withStyles(styleSheet)(FundingSourceChargeLimit);
